import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { ConfigProvider, theme } from "antd";
import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { FaqCategoriesToolbar } from "src/components/faq/FaqCategoriesToolbar";
import { faqSectionSortId } from "src/components/faq/faqDndIds";
import { FaqEmptyCategories } from "src/components/faq/FaqEmptyCategories";
import { FaqLoadError } from "src/components/faq/FaqLoadError";
import { FaqLoadingState } from "src/components/faq/FaqLoadingState";
import { FaqPanelHeader } from "src/components/faq/FaqPanelHeader";
import { FaqPanelLocked } from "src/components/faq/FaqPanelLocked";
import { FaqSaveFooter } from "src/components/faq/FaqSaveFooter";
import { FaqSectionCard } from "src/components/faq/FaqSectionCard";
import { FaqSortableSection } from "src/components/faq/FaqSortableSection";
import { FaqStatusBanner } from "src/components/faq/FaqStatusBanner";
import { actorFromAuth } from "src/lib/activityService";
import { FAQ_FIRESTORE_PATH } from "src/lib/faqService";
import { canReadService, canWriteService, ServiceDomain } from "src/lib/roles";
import { selectUser, selectUserProfile } from "src/store/authSlice";
import { useFaqStore } from "src/store/faqStore";
import { Constants } from "../constant";

const AppFaqPanel: React.FC = () => {
  const user = useSelector(selectUser);
  const userProfile = useSelector(selectUserProfile);
  const activityActor = actorFromAuth(user, userProfile);

  const canView =
    userProfile && canReadService(userProfile.role, ServiceDomain.AppFaq);
  const canManage =
    userProfile && canWriteService(userProfile.role, ServiceDomain.AppFaq);

  const form = useFaqStore((s) => s.form);
  const loading = useFaqStore((s) => s.loading);
  const error = useFaqStore((s) => s.error);
  const success = useFaqStore((s) => s.success);
  const load = useFaqStore((s) => s.load);
  const clearSuccess = useFaqStore((s) => s.clearSuccess);
  const moveSectionByIndex = useFaqStore((s) => s.moveSectionByIndex);
  const reset = useFaqStore((s) => s.reset);
  const setCanManage = useFaqStore((s) => s.setCanManage);
  const setActivityActor = useFaqStore((s) => s.setActivityActor);

  useEffect(() => {
    setCanManage(!!canManage);
  }, [canManage, setCanManage]);

  useEffect(() => {
    setActivityActor(activityActor ?? null);
  }, [activityActor, setActivityActor]);

  useEffect(() => {
    if (canView) void load();
  }, [canView, load]);

  useEffect(() => {
    if (!success) return;
    const id = window.setTimeout(() => clearSuccess(), 5000);
    return () => window.clearTimeout(id);
  }, [success, clearSuccess]);

  useEffect(() => {
    return () => reset();
  }, [reset]);

  const sectionSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
      },
    }),
  );

  const handleSectionDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    const currentForm = useFaqStore.getState().form;
    if (!active || !over || !currentForm || active.id === over.id) return;
    const sectionIds = currentForm.faq.map((_, i) => faqSectionSortId(i));
    const oldIndex = sectionIds.indexOf(String(active.id));
    const newIndex = sectionIds.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    moveSectionByIndex(oldIndex, newIndex);
  };

  if (!canView) {
    return <FaqPanelLocked description={Constants.APP_FAQ_DESCRIPTION} />;
  }

  const panel = (
    <div
      className="mx-auto max-w-3xl pb-10 pt-2 sm:max-w-4xl sm:pt-4"
      style={{ fontFamily: "'Nunito', system-ui, sans-serif" }}
    >
      <FaqPanelHeader
        description={Constants.APP_FAQ_DESCRIPTION}
        firestorePath={FAQ_FIRESTORE_PATH}
      />

      {success && (
        <FaqStatusBanner variant="success">{success}</FaqStatusBanner>
      )}

      {error && <FaqStatusBanner variant="error">{error}</FaqStatusBanner>}

      {loading && !form ? (
        <FaqLoadingState />
      ) : !form ? (
        <FaqLoadError />
      ) : (
        <>
          <FaqCategoriesToolbar />

          {form.faq.length === 0 ? (
            <FaqEmptyCategories />
          ) : canManage ? (
            <DndContext
              id="faq-sections-dnd"
              sensors={sectionSensors}
              collisionDetection={closestCenter}
              modifiers={[restrictToVerticalAxis]}
              onDragEnd={handleSectionDragEnd}
            >
              <SortableContext
                items={form.faq.map((_, i) => faqSectionSortId(i))}
                strategy={verticalListSortingStrategy}
              >
                <ul className="flex flex-col gap-6">
                  {form.faq.map((section, sIdx) => (
                    <FaqSortableSection
                      key={`${section.id}-${sIdx}`}
                      id={faqSectionSortId(sIdx)}
                      disabled={false}
                    >
                      {(sortable) => (
                        <FaqSectionCard
                          sectionIndex={sIdx}
                          sectionSortable={sortable}
                        />
                      )}
                    </FaqSortableSection>
                  ))}
                </ul>
              </SortableContext>
            </DndContext>
          ) : (
            <ul className="flex flex-col gap-6">
              {form.faq.map((section, sIdx) => (
                <FaqSectionCard
                  key={`${section.id}-${sIdx}`}
                  sectionIndex={sIdx}
                />
              ))}
            </ul>
          )}

          {canManage && <FaqSaveFooter />}
        </>
      )}
    </div>
  );

  return (
    <ConfigProvider theme={{ algorithm: theme.darkAlgorithm }}>
      {panel}
    </ConfigProvider>
  );
};

export default AppFaqPanel;
